import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import {
  selectIsEmailVerified,
  setIsEmailVerified,
} from '@/app/redux/slices/sessionSlice';
import { handleLogout } from '@/common/utils/auth';
import getDefaultDashboardRoute from '@/common/utils/defaultDashboardRoute';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import api from '@/db/apiClient';
import { SignOutIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@shadcn/components/card';
import { Input } from '@shadcn/components/input';
import { AlertCircle, CheckCircle2, Loader2, Mail } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function VerifyEmail() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isEmailVerified = useAppSelector(selectIsEmailVerified);

  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [code, setCode] = useState<string>('');

  const handleResendVerificationEmail = async () => {
    setResendLoading(true);
    setMessage(null);
    try {
      const user = api.asEducator.auth.currentUser;
      if (user) {
        await api.asEducator.generateVerificationCode({ uid: user.uid });
        setMessage({
          type: 'success',
          text: 'Verification code sent! Please check your inbox.',
        });
        trackMixpanelEvent('User resent verification email', {
          userEmail: user.email,
          code,
        });
      } else {
        setMessage({
          type: 'error',
          text: 'No user is currently signed in.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to send verification code. Please try again.',
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      setMessage({
        type: 'error',
        text: 'Please enter a verification code.',
      });
      return;
    }

    setVerifyLoading(true);
    setMessage(null);
    try {
      const user = api.asEducator.auth.currentUser;
      if (user) {
        const result = await api.asEducator.verifyEmailCode({
          uid: user.uid,
          code,
        });

        if (result.success) {
          dispatch(setIsEmailVerified(true));
          setMessage({
            type: 'success',
            text: 'Email verified successfully!',
          });
          trackMixpanelEvent('User verified email', {
            userEmail: user.email,
            verificationCode: code,
          });
        } else {
          setMessage({
            type: 'error',
            text: 'Invalid verification code. Please try again.',
          });
        }
      } else {
        setMessage({
          type: 'error',
          text: 'No user is currently signed in.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to verify email. Please try again.',
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <Card className="w-full max-w-md mx-auto relative">
        <div className="absolute top-4 right-4">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={handleLogout}
          >
            <SignOutIcon className="h-4 w-4" />
            Sign out
          </Button>
        </div>
        <CardHeader className="space-y-1 flex flex-col items-center pt-16">
          {isEmailVerified ? (
            <CheckCircle2 className="h-12 w-12 text-green-500 mb-2" />
          ) : (
            <Mail className="h-12 w-12 text-primary mb-2" />
          )}
          <h2 className="text-2xl font-semibold text-center">
            {isEmailVerified ? 'Email verified successfully!' : 'Almost there!'}
          </h2>
        </CardHeader>

        <CardContent className="space-y-4">
          {isEmailVerified ? (
            <div className="flex justify-center">
              <Button onClick={() => navigate(getDefaultDashboardRoute())}>
                Continue
              </Button>
            </div>
          ) : (
            <>
              <p className="text-center text-sm text-muted-foreground">
                We just sent a code to verify your email. Please enter it below.
              </p>
              <p className="text-center text-xs text-muted-foreground">
                (Remember to check your spam folder if you don't see the email
                or double check your email address (typos happen 😊))
              </p>
              {message && !isEmailVerified && (
                <Alert
                  variant={message.type === 'error' ? 'destructive' : 'default'}
                >
                  <AlertCircle className="h-4 w-4 ml-2" />
                  <AlertDescription>{message.text}</AlertDescription>
                </Alert>
              )}
              <Input
                type="text"
                placeholder="Enter verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full"
                maxLength={6}
                disabled={verifyLoading || resendLoading}
              />
              <div className="space-y-2">
                <Button
                  className="w-full"
                  onClick={handleVerifyCode}
                  disabled={verifyLoading || resendLoading || !code.trim()}
                >
                  {verifyLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Verify Code
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleResendVerificationEmail}
                  disabled={verifyLoading || resendLoading}
                >
                  {resendLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Resend Verification Code
                </Button>
              </div>
            </>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-4 text-center text-sm text-muted-foreground">
          <div className="space-y-1">
            <p>Need help?</p>
            <p>
              Reach out:{' '}
              <a
                href="mailto:rupert@spellcaster.education"
                className="text-primary hover:underline font-medium"
              >
                rupert@spellcaster.education
              </a>
            </p>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
